// "Recent saved files" — the first row of the box's Attach-file pane and the submenu behind it
// (busy-my-web#408, Figma 40008539-200927 / 201021 / 201209, 40000923-63242).
//
// The frame lists the things the user has already saved into Busy — files and links — newest
// first, each with a tick box; ticking one puts it on the Work-board, selected (the Briefing's
// "Selected files" section that also listed it was removed in #585). The list itself is the user's DOCUMENTS (the BFF's `documents` page, the same
// answer the Attachments briefing reads), so nothing new is invented on this side: a document is a
// row, its filename is the label, and its Drive id is what the existing attach flow links.
//
// Pure, so the reading — the order, the labels, the glyphs, what a tick stages — is checked
// without mounting the pane or touching the network.

// How many rows the submenu shows before "See all" takes over (the frame draws four; a couple more
// fit the pane and the "See all" line is there for the rest).
export const RECENT_LIMIT = 6

/** When a document was last saved — `updateOn` if it has been touched, else `createdOn`. */
export const savedAt = (doc) => {
  const raw = doc?.updateOn || doc?.updatedOn || doc?.createdOn || doc?.createdDate || null
  if (!raw) return null
  const d = raw instanceof Date ? raw : new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * The Drive file id the attach flow links by, or ''. A document row carries it as its external
 * entity; a row without one still LISTS (it is a real saved file), it simply cannot be linked to
 * the entity being composed — see the dev note on the missing field.
 */
export const driveIdOf = (doc) =>
  String(doc?.externalEntityId || doc?.externalId || doc?.fileId || '')

/** A saved LINK rather than a file: the frame's "selantoll.com" rows. */
export const linkOf = (doc) => String(doc?.url || doc?.link || doc?.webLink || '')

/**
 * One row of the submenu: `{ id, name, kind, url, fileId, contentType }`. `kind` picks the glyph —
 * a link glyph for a saved link, the file glyph otherwise.
 */
export const recentRow = (doc) => {
  const url = linkOf(doc)
  const name = String(doc?.filename || doc?.fileName || doc?.name || '').trim()
  return {
    id: String(doc?.id || doc?.documentId || driveIdOf(doc) || ''),
    name: name || url || 'Attachment',
    kind: url && !name ? 'link' : 'file',
    url,
    fileId: driveIdOf(doc),
    contentType: doc?.contentType || '',
    savedAt: savedAt(doc)
  }
}

/**
 * The submenu's list: every saved document as a row, newest first, capped at `limit`. Documents
 * without an id are dropped (they cannot be ticked, and the frame has no row for a thing with no
 * identity); ties keep the order the server sent, which is already newest-first.
 */
export const recentSavedFiles = (docs, { limit = RECENT_LIMIT } = {}) => {
  const rows = (Array.isArray(docs) ? docs : [])
    .map((d, index) => ({ row: recentRow(d), index }))
    .filter(({ row }) => !!row.id)
  rows.sort((a, b) => {
    const at = a.row.savedAt ? a.row.savedAt.getTime() : null
    const bt = b.row.savedAt ? b.row.savedAt.getTime() : null
    if (at === bt) return a.index - b.index
    if (at === null) return 1
    if (bt === null) return -1
    return bt - at
  })
  return rows.slice(0, Math.max(0, limit)).map(({ row }) => row)
}

/**
 * The rows that MATCH some words — "Find related files" on a highlighted sentence
 * (busy-my-web#414). The documents page has no text query of its own, so the match is made here,
 * over the page already in hand: a row matches when its name carries any word of the query that is
 * worth matching on (three letters or more). An empty query matches everything, which is the plain
 * "recent saved files" list.
 */
export const matchRecentRows = (rows, query) => {
  const words = String(query || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .filter((w) => w.length >= 3)
  const list = Array.isArray(rows) ? rows : []
  if (!words.length) return list
  return list.filter((r) => {
    const hay = `${r?.name || ''} ${r?.url || ''}`.toLowerCase()
    return words.some((w) => hay.includes(w))
  })
}

/**
 * A ticked row as the box stages a file: the same record shape a Drive pick makes, so the
 * Work-board chip, the Briefing's attachments fact and the save's attach flow all treat it exactly
 * as they treat any other picked file (SmartBox.vue `pickDriveFile`).
 */
export const stagedRecentFile = (row) => ({
  tempId: `recent-${row?.id || ''}`,
  name: row?.name || 'Attachment',
  uploading: false,
  error: false,
  fileId: row?.fileId || '',
  filename: row?.name || 'Attachment',
  contentType: row?.contentType || '',
  // Where the row came from, so a second tick can find the chip it made.
  recentId: String(row?.id || ''),
  url: row?.url || ''
})

/** Is this row already staged? Matches on the tempId `stagedRecentFile` mints. */
export const isRecentPicked = (row, files) =>
  (Array.isArray(files) ? files : []).some((f) => f && f.tempId === `recent-${row?.id || ''}`)

export default recentSavedFiles
